<template>
  <div class="prompt-form-container">
    <form @submit.prevent="handleSubmit" class="prompt-form">
      <div class="form-header">
        <h2 class="form-title"><span class="green">Create</span> <span class="yellow">Your </span> <span class="blue">Drawing</span></h2>
        <p class="form-subtitle">Describe what you'd like to color and we'll generate it for you</p>
      </div>
  
      <div class="form-group">
        <label for="prompt" class="form-label">
          <span class="label-text">Make me a coloring of:</span>
          <span class="label-decoration">✨</span>
        </label>
        <div class="textarea-container">
          <textarea
            id="prompt"
            v-model="form.prompt"
            placeholder="A T-rex having a birthday party with his dinosaurs friends"
            rows="4"
            class="form-textarea"
            required
          ></textarea>
          <div class="textarea-border"></div>
        </div>
      </div>
  
      <div class="form-actions">
        <button 
          v-if="!loading && !alertStore.alert" 
          type="submit" 
          class="generate-btn"
        >
          <span class="btn-icon">🎨</span>
          <span class="btn-text">Generate</span>
          <span class="btn-icon">🎨</span>
          <div class="btn-shine"></div>
        </button>
        
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">Creating your masterpiece...</p>
        </div>
      </div>  
      <AlertPop/>
    </form>
    <!-- Modal -->
          <div v-if="isModalOpen" class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3 class="modal-title">Your Coloring Page is Ready!</h3>
            <button @click="closeModal" class="modal-close">&times;</button>
          </div>
          
          <div class="modal-body">
            <img v-if="modalImage" :src="modalImage" alt="Generated coloring page" class="modal-image"/>
          </div>
          
          <div class="modal-footer">
            <button @click="printImage" class="modal-btn print-btn">
              <span class="btn-icon">🖨️</span>
              Print
            </button>
            <button @click="closeModal" class="modal-btn close-btn">
              <span class="btn-icon">✨</span>
              Done
            </button>
          </div>
        </div>
      </div>
  </div>
  </template>
  
  <script lang="ts">
  import { defineComponent, ref} from 'vue'
  import { useAuthStore } from '../stores/auth';
  import { PrintPhoto } from '@/service/services';
  import { v4 as uuidv4 } from 'uuid';
  import { useAlertStore } from '@/stores/alert';
  import AlertPop from './AlertPop.vue';
  
  export default defineComponent({
    name: 'PromptForm',
    components: {AlertPop},
    emits: ['refresh-needed'],
  
    setup(_, { emit }) {
      const form = ref({
        prompt: '',
      });
  
      const loading = ref(false);
      const error = ref<string | null>(null);
      const submittedData = ref<{ prompt: string; image_url: string } | null>(null);
      const isModalOpen = ref<boolean>(false);
      const modalImage = ref<string | null>(null);
      const apiUrl = process.env.VUE_APP_DJANGO_BACKEND
      const alertStore = useAlertStore();
  
      const openModal = (image:string) => {
          modalImage.value= image
          isModalOpen.value = true; 
        }
  
      const closeModal = () => {
          isModalOpen.value = false; 
      };
  
      const printImage = () => {
        if (modalImage.value) {
          console.log(modalImage.value)
          PrintPhoto(modalImage.value)
        }
      }
  
    const handleSubmit = async () => {
    const authStore = useAuthStore();
    const accessToken = authStore.session?.access_token;
  
    let visitorId: string | null = null;
  
    if (!accessToken) {
      visitorId = localStorage.getItem("visitor_id");
      if (!visitorId) {
        visitorId = uuidv4();
        localStorage.setItem("visitor_id", visitorId);
      }
      console.log("Visitor ID:", visitorId);
    }
  
    try {
      loading.value = true;
      error.value = null;
  
      const data = {
        prompt: form.value.prompt,
      };
  
      const response = await fetch(`${apiUrl}/api/prompts/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(visitorId && { 'X-Visitor-ID': visitorId }),
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
        body: JSON.stringify(data),
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        error.value = errorData.detail || 'Request failed.';
        alertStore.show(
          errorData.detail || 'Request failed. Please try again.');
        return;
      }
  
      const responseData = await response.json();
      submittedData.value = {
        prompt: responseData.prompt,
        image_url: responseData.image_url,
      };
  
      if (responseData.image_url) {
        await preloadImage(responseData.image_url);
        openModal(responseData.image_url);
      }
  
    } catch (err) {
      error.value = 'Error submitting form. Please try again.';
      console.error('Error submitting form:', err);
    } finally {
      emit('refresh-needed');
      loading.value = false;
    }
  };
  
      const preloadImage = (url: string): Promise<void> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.src = url;
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Image failed to load'));
        });
      };
  
      return {
        form,
        loading,
        error,
        submittedData,
        handleSubmit,
        openModal,
        closeModal,
        printImage,
        modalImage,
        isModalOpen,
        alertStore,
      };
    },
  })
  </script>
  
  <style scoped>

  .blue {
    color:#578FCA;
    font-weight: 700;
  }

  .green {
    color:#8EB486;
    font-weight: 700;
  }

  .yellow {
    color: #FFAF47;
    font-weight: 700;
  }


  .prompt-form-container {
    max-width: 600px;
    margin: 2rem auto;
    padding: 0 1rem;
  }
  
  .prompt-form {
    border-radius: 10px;
    padding: 2.5rem;
    box-shadow: 
      0 5px 0px 0px rgba(0, 0, 0, 0.1),
      0 5px 5px 1px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.2);
    position: relative;
    overflow: hidden;
  }
  
  .prompt-form::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background:  #6dd496;
    background-size: 200% auto;
    animation: shimmer 3s linear infinite;
  }
  
  @keyframes shimmer {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  
  .form-header {
    text-align: center;
    margin-bottom: 2rem;
  }
  
  .form-title {
    font-size: 2rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
    background-clip: text;
  }
  
  .form-subtitle {
    color: #64748b;
    font-size: 1.1rem;
    margin: 0;
    line-height: 1.5;
  }
  
  .form-group {
    margin-bottom: 2rem;
  }
  
  .form-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    font-weight: 600;
    color: #374151;
    font-size: 1.1rem;
  }
  
  .label-decoration {
    font-size: 1.2rem;
  }
  
  .textarea-container {
    position: relative;
  }
  
  .form-textarea {
    width: 100%;
    padding: 1.25rem;
    font-size: 1rem;
    border: 2px solid #e2e8f0;
    border-radius: 16px;
    background: #ffffff;
    transition: all 0.3s ease;
    font-family: inherit;
    line-height: 1.6;
    resize: vertical;
    min-height: 120px;
    box-sizing: border-box;
  }
  
  .form-textarea:focus {
    outline: none;
    border-color: #42a8b4;
    box-shadow: 
      0 0 0 4px rgba(66, 168, 180, 0.1),
      0 10px 25px -5px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
  
  .form-textarea::placeholder {
    color: #9ca3af;
    font-size: 0.95rem;
    line-height: 1.5;
  }
  
  .form-actions {
    text-align: center;
  }
  
  .generate-btn {
    position: relative;
    background: #A1E3F9;
    color: #1e293b;
    border: none;
    padding: 1rem 2.5rem;
    border-radius: 10px;
    font-size: 1.1rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    font-family: inherit;
    text-transform: none;
    letter-spacing: 0.5px;
    box-shadow: 
      0 10px 20px rgba(66, 168, 180, 0.3),
      0 4px 8px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }
  
  .generate-btn:hover {
    transform: translateY(-3px);
    box-shadow: 
      0 15px 30px rgba(66, 168, 180, 0.4),
      0 8px 16px rgba(0, 0, 0, 0.15);
  }
  
  .generate-btn:active {
    transform: translateY(-1px);
  }
  
  .btn-icon {
    font-size: 1.2rem;
  }
  
  .btn-shine {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: left 0.5s;
  }
  
  .generate-btn:hover .btn-shine {
    left: 100%;
  }
  
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    padding: 2rem 0;
  }
  
  .loading-spinner {
    width: 50px;
    height: 50px;
    border: 4px solid #e2e8f0;
    border-top: 4px solid #42a8b4;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .loading-text {
    color: #42a8b4;
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
    background: linear-gradient(90deg, #42a8b4, #6dd496, #42a8b4);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: shimmer 2s linear infinite;
  }
  
  /* Modal Styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(8px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    animation: fadeIn 0.3s ease;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  .modal-content {
    background: white;
    border-radius: 24px;
    max-width: 90vw;
    max-height: 90vh;
    overflow: hidden;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    animation: slideUp 0.3s ease;
    display: flex;
    flex-direction: column;
  }
  
  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(30px) scale(0.95);
    }
    to { 
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e2e8f0;
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  }
  
  .modal-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
  }
  
  .modal-close {
    background: none;
    border: none;
    font-size: 2rem;
    cursor: pointer;
    color: #64748b;
    padding: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
  }
  
  .modal-close:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
  
  .modal-body {
    padding: 1rem;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8fafc;
  }
  
  .modal-image {
    max-width: 100%;
    max-height: 60vh;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  }
  
  .modal-footer {
    padding: 1.5rem 2rem;
    display: flex;
    gap: 1rem;
    border-top: 1px solid #e2e8f0;
    background: white;
  }
  
  .modal-btn {
    flex: 1;
    padding: 0.875rem 1.5rem;
    border: none;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-family: inherit;
  }
  
  .print-btn {
    background: linear-gradient(135deg, #6dd496 0%, #4ade80 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(109, 212, 150, 0.3);
  }
  
  .print-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(109, 212, 150, 0.4);
  }
  
  .close-btn {
    background: linear-gradient(135deg, #e7ea80 0%, #facc15 100%);
    color: #1e293b;
    box-shadow: 0 4px 12px rgba(231, 234, 128, 0.3);
  }
  
  .close-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(231, 234, 128, 0.4);
  }
  
  /* Responsive Design */
  @media (max-width: 768px) {
    .prompt-form-container {
      margin: 1rem auto;
      padding: 0 0.5rem;
    }
    
    .prompt-form {
      padding: 1.5rem;
      border-radius: 16px;
    }
    
    .form-title {
      font-size: 1.5rem;
    }
    
    .form-subtitle {
      font-size: 1rem;
    }
    
    .generate-btn {
      padding: 0.875rem 2rem;
      font-size: 1rem;
    }
    
    .modal-content {
      margin: 1rem;
      max-width: calc(100vw - 2rem);
    }
    
    .modal-header,
    .modal-footer {
      padding: 1rem 1.5rem;
    }
    
    .modal-footer {
      flex-direction: column;
    }
    
    .modal-image {
      max-height: 50vh;
    }
  }
  </style>